import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {AntragTicketSchoolInfoDto} from "../../../../models/antrag-ticket/AntragTicketSchoolInfoDto";
import {Observable, of, Subject, throwError} from "rxjs";
import {TextbausteinService} from "../../../../@core/services/textbaustein/textbaustein.service";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {FieldConfigService} from "../../../../@core/services/field-config/field-config.service";
import {
  AntragTicketSchoolInfoService
} from "../../../../@core/services/antrag-ticket/antrag-ticket-school-info.service";
import {CustomToastService} from "../../../../@core/utils/custom-toast.service";
import {pairwise, startWith, tap} from "rxjs/operators";
import {KeyString} from "../../../../models/textbaustein/KeyString";
import {SchoolType} from "../../../../models/schule/SchoolType";
import {PFLICHTFELDMARKER} from "../../../../../assets/constants/constants";
import {SchuleDTO} from "../../../../models/schule/SchuleDTO";
import {Wegtyp} from "../../../../models/antrag-ticket/Wegtyp";
import {AntragTicketSchoolInfoUpdateDto} from "../../../../models/antrag-ticket/AntragTicketSchoolInfoUpdateDto";
import {JsonHelper} from "../../../../@core/utils/json-helper";
import {SchuljahrDTO} from "../../../../models/schuljahr/SchuljahrDTO";
import {KlasseDTO} from "../../../../models/klasse/KlasseDTO";
import {BildungsgangDTO} from "../../../../models/bildungsgang/BildungsgangDTO";
import {SelectComponent} from "../../../../shared/form/select/select.component";
import {InputComponent} from "../../../../shared/form/input/input.component";
import {DatePipe} from "@angular/common";

@Component({
  selector: 'app-school-info',
  imports: [
    TranslatePipe,
    SelectComponent,
    InputComponent,
    DatePipe,
    ReactiveFormsModule
  ],
  templateUrl: './school-info.component.html',
  styleUrl: './school-info.component.scss'
})
export class SchoolInfoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() antragId;
  @Input() existsSessionStorageTextbausteine;
  @Input() effectivelyVisible;
  @Input() effectiveValidators;
  @Input() fieldConfig;
  @Input() isEntwurf;
  @Input() wegtyp;
  @Input() minDate;
  @Input() schoolList;
  @Input() schoolYearList;
  @Input() classList;
  @Input() courseList;
  @Input() schoolLevelList;
  @Input() schoolDaysAWeek;
  @Input() courseDate;

  @Output() schoolChangeEvent = new EventEmitter<any>();
  @Output() schoolRemappedEvent = new EventEmitter<void>();
  @Output() schoolYearChangeEvent = new EventEmitter<any>();
  @Output() schoolInfoInitializedEvent = new EventEmitter<void>();

  form: UntypedFormGroup

  schoolInfo: AntragTicketSchoolInfoDto;

  showSchoolLevel = false;

  optionSchoolLevel: {translation: string, name: string}[];

  optionSchoolType;

  updateSchoolInfoLoading = false;

  waitForSchoolInfoUpdate$ = new Subject<void>();

  constructor(
    protected readonly fb: UntypedFormBuilder,
    protected readonly textbausteinService: TextbausteinService,
    protected readonly translateService: TranslateService,
    protected readonly fieldConfigService: FieldConfigService,
    protected readonly antragTicketSchoolInfoService: AntragTicketSchoolInfoService,
    protected readonly customToastService: CustomToastService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.antragId) {
      this.getSchoolInfo();
      this.setValidators();
    }
    if (changes.schoolList) {
      this.remapSchoolIfNeeded();
    }
    if (changes.classList || changes.courseList) {
      this.updateSchoolLevelVisibility();
      this.remapClassIfNeeded();
      this.remapCourseIfNeeded();
    }
    if (changes.schoolDaysAWeek) {
      this.getFormItem('schoolDaysAWeek')?.patchValue(changes.schoolDaysAWeek.currentValue);
    }
    if (changes.courseDate) {
      this.getFormItem('courseDate')?.patchValue(changes.courseDate.currentValue);
    }
    if (changes.schoolLevelList) {
      this.loadOptionSchoolLevel();
    }
    if (changes.schoolYearList) {
      this.remapSchoolYearIfNeeded();
    }
    if (changes.effectiveValidators || changes.wegtyp || changes.isEntwurf || changes.schoolList) {
      this.setValidators();
    }
  }

  /**
   * Re-maps form values when input lists arrive late (due to async loading in the parent).
   * Without these, selections are lost on reload because the UUID-to-ID mapping fails on an empty list.
   */
  private remapSchoolIfNeeded(): void {
    if (!this.schoolInfo?.schoolRef?.schoolId) {
      return;
    }
    const currentFormValue = this.getFormItem('schoolId')?.value;
    if (currentFormValue) {
      return;
    }
    const mapped = this.mapSchoolUUIDToId(this.schoolInfo.schoolRef.schoolId);
    if (mapped) {
      this.getFormItem('schoolId')?.patchValue(mapped, {emitEvent: false});
      this.setValidators();
      // emitEvent: false suppresses schoolChangeEvent (which would reset user data) - let the parent
      // re-evaluate school dependent visibility/lists instead.
      this.schoolRemappedEvent.emit();
    }
  }

  private remapSchoolYearIfNeeded(): void {
    if (!this.schoolInfo?.schoolYearRef?.schoolYearId) {
      return;
    }
    const currentFormValue = this.getFormItem('schoolYearId')?.value;
    if (currentFormValue) {
      return;
    }
    const mapped = this.mapSchoolYearUUIDToId(this.schoolInfo.schoolYearRef.schoolYearId);
    if (mapped) {
      this.getFormItem('schoolYearId')?.patchValue(mapped, {emitEvent: false});
      this.setValidators();
      // schoolYearId was not yet mappable when schoolInfoInitializedEvent first fired (schoolYearList
      // arrived late), so the parent's Vertragsbeginn/disableGueltigAb calculation ran without a
      // resolvable Schuljahr and is now stale - notify it to recompute now that the id is known.
      this.schoolInfoInitializedEvent.emit();
    }
  }

  private remapClassIfNeeded(): void {
    if (!this.schoolInfo?.classRef?.classId) {
      return;
    }
    const currentFormValue = this.getFormItem('classId')?.value;
    if (currentFormValue) {
      return;
    }
    const mapped = this.mapClassUUIDToId(this.schoolInfo.classRef.classId);
    if (mapped) {
      this.getFormItem('classId')?.patchValue(mapped, {emitEvent: false});
      this.setValidators();
    }
  }

  private remapCourseIfNeeded(): void {
    if (!this.schoolInfo?.courseRef?.courseId) {
      return;
    }
    const currentFormValue = this.getFormItem('courseId')?.value;
    if (currentFormValue) {
      return;
    }
    const mapped = this.mapCourseUUIDToId(this.schoolInfo.courseRef.courseId);
    if (mapped) {
      this.getFormItem('courseId')?.patchValue(mapped, {emitEvent: false});
      this.setValidators();
    }
  }

  ngOnInit() {
    this.form = this.fb.group({
      schoolId: [''],
      schoolYearId: [''],
      classId: [''],
      courseId: [''],
      schoolLevel: [''],
      schoolType: [''],
      schoolDaysAWeek: [this.schoolDaysAWeek],
      courseDate: [this.courseDate]
    });

    this.createChangeEvents();

    this.getSchoolInfo();

    this.optionSchoolType = [{id: null, name: "-" },
      {id:"ELEMENTARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.ELEMENTARY") },
      {id:"GENERAL_SECONDARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.GENERAL_SECONDARY") },
      {id:"INTERMEDIATE_SECONDARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.INTERMEDIATE_SECONDARY") },
      {id:"GRAMMAR", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.GRAMMAR") },
      {id:"COMPREHENSIVE", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.COMPREHENSIVE") },
      {id:"VOCATIONAL", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.VOCATIONAL") },
      {id:"SPECIAL", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.SPECIAL") },
      {id:"SPECIAL_INTELLECTUAL_DEVELOPMENT", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.SPECIAL_INTELLECTUAL_DEVELOPMENT") },
      {id:"DENOMINATIONAL_ELEMENTARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.DENOMINATIONAL_ELEMENTARY") },
      {id:"CATHOLIC_GENERAL_SECONDARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.CATHOLIC_GENERAL_SECONDARY") },
      {id:"OTHER", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.OTHER") }
    ];

    this.loadOptionSchoolLevel();

    this.updateSchoolLevelVisibility();
    this.setValidators();
  }

  loadOptionSchoolLevel() {
    this.optionSchoolLevel = [
      {translation: 'EMPTY', name: "" }
    ];

    for (const schoolLevel of this.schoolLevelList) {
      this.optionSchoolLevel.push(
        {translation: schoolLevel, name: schoolLevel },
      )
    }
  }

  updateSchoolLevelVisibility() {
    this.showSchoolLevel = this.classList.length == 0 && this.courseList.length == 0 && this.getFormItem('schoolId')?.value;
  }

  createChangeEvents() {
    this.getFormItem('schoolId')?.valueChanges
      .pipe(startWith(null),pairwise())
      .subscribe(([prev, next]: [any, any]) => {
        if (next!==prev) {
          this.resetFields();
          this.schoolChange();
          const school = this.schoolList.find(s => s.id === this.getFormItem('schoolId')?.value);
          this.getFormItem('schoolType')?.patchValue(school ? school.schoolType : null);
          this.setValidators();
        }
      });

    this.getFormItem('schoolYearId')?.valueChanges
      .pipe(startWith(null),pairwise())
      .subscribe(([prev, next]: [any, any]) => {
        if (next!==prev) {
          this.schoolYearChangeEvent.emit(this.getFormItem('schoolYearId')?.value);
          this.setValidators();
        }
      });
  }

  getSchoolInfo() {
    if (!this.antragId) {
      return;
    }
    this.antragTicketSchoolInfoService.getSchoolInfoByAntrag(this.antragId)
      .subscribe({
        next: schoolInfo => {
          this.schoolInfo = schoolInfo;
          this.updateForm();
          this.schoolInfoInitializedEvent.emit();
        },
        error: err => {
          console.error('Failed to load school info:', err);
        }
      });
  }

  updateForm() {
    const schoolId = this.schoolInfo?.schoolRef?.schoolId;
    const schoolYearId = this.schoolInfo?.schoolYearRef?.schoolYearId;
    const classId = this.schoolInfo?.classRef?.classId;
    const courseId = this.schoolInfo?.courseRef?.courseId;
    this.form.patchValue({
      schoolId: this.mapSchoolUUIDToId(schoolId) || '',
      schoolYearId: this.mapSchoolYearUUIDToId(schoolYearId) || '',
      classId: this.mapClassUUIDToId(classId) || '',
      courseId: this.mapCourseUUIDToId(courseId) || '',
      schoolLevel: this.schoolInfo?.schoolLevel || '',
      schoolType: this.schoolInfo?.schoolType || '',
      schoolDaysAWeek: this.schoolDaysAWeek || '',
      courseDate: this.courseDate || ''
    });
    this.setValidators();
  }

  getTextbausteinOrTranslate(textbausteinKey: KeyString, translateKey: string): string {
    return this.existsSessionStorageTextbausteine?
      this.textbausteinService.printTextbausteinByKey(textbausteinKey, translateKey?
        this.translateService.instant(translateKey) : '') : '';
  }

  getFieldLabelOrTranslate(fieldName: string, translateKey: string): string {
    return ''+(this.isRequiredByConfig(fieldName)? PFLICHTFELDMARKER : '')
      + this.fieldConfigService.getFieldLabel(fieldName, this.fieldConfig, translateKey? this.translateService.instant(translateKey) : '');
  }

  isRequiredByConfig(fieldName: string) : boolean {
    return this.fieldConfigService.getFieldFromFieldConfig(fieldName, this.fieldConfig)?.required === true;
  }

  getFormItem(s: string) : AbstractControl {
    return this.form?.get(s);
  }

  isBerufskolleg(id): boolean {
    if (!id) {
      return false;
    }
    return this.schoolList?.find((schule: SchuleDTO) => schule.id === id)?.schoolType === SchoolType.VOCATIONAL;
  }

  setValidators() {
    if (!this.form) {
      return;
    }
    for (const controlName of Object.keys(this.form.controls)) {
      this.getFormItem(controlName)?.clearValidators();
    }
    // effectiveValidators is already filtered by effectivelyVisible in the parent (updateEffectiveValidators()),
    // so no additional visibility check is needed here.
    this.effectiveValidators?.forEach((field: string) => this.setValidatorForField(field));
    for (const controlName of Object.keys(this.form.controls)) {
      this.getFormItem(controlName)?.updateValueAndValidity({emitEvent: false});
    }
  }

  private setValidatorForField(field: string): void {
    // All validators were already cleared in setValidators(), so only the required ones have to be set here.
    if (!this.isEntwurf) {
      return;
    }
    const schoolId = this.getFormItem('schoolId')?.value;
    const isSchulwegOrBeides = [Wegtyp.SCHULWEG, Wegtyp.BEIDES].includes(this.wegtyp);
    if (field === 'schuleId') {
      this.getFormItem('schoolId')?.setValidators([Validators.required]);
    } else if (field === 'schuljahrId' && isSchulwegOrBeides && schoolId) {
      this.getFormItem('schoolYearId')?.setValidators([Validators.required]);
    } else if (field === 'klasseId' && isSchulwegOrBeides && schoolId && !this.isBerufskolleg(schoolId)) {
      this.getFormItem('classId')?.setValidators([Validators.required]);
    } else if (field === 'bildungsgangId' && isSchulwegOrBeides && schoolId && this.isBerufskolleg(schoolId)) {
      this.getFormItem('courseId')?.setValidators([Validators.required]);
    } else if (field === 'bildungsgangdatum' && isSchulwegOrBeides && schoolId && this.isBerufskolleg(schoolId)) {
      this.getFormItem('courseDate')?.setValidators([Validators.required]);
    } else if (field === 'schultageJeWoche' && this.wegtyp === Wegtyp.BEIDES) {
      this.getFormItem('schoolDaysAWeek')?.setValidators([Validators.required]);
    }
  }

  /**
   * @param validate whether required Schulangaben must be present. Saving a draft (ENTWURF) passes
   *                 false, so incomplete Schulangaben can be stored; only submitting validates.
   */
  updateSchoolInfo(validate = true): Observable<AntragTicketSchoolInfoDto> {
    this.updateSchoolInfoLoading = true;
    if (!this.antragId) {
      console.log('Cannot update school info: antragId is missing');
      this.customToastService.showError(this.translateService.instant('TICKETDETAIL.SCHOOL_INFO.ANTRAG_ID_MISSING'));
      this.updateSchoolInfoLoading = false;
      return throwError(() => new Error('Cannot update school info: antragId is missing'));
    }
    if (validate && this.form.invalid) {
      this.updateSchoolInfoLoading = false;
      this.customToastService.showError(this.translateService.instant('TICKETDETAIL.SCHOOL_INFO.UPDATE_ERROR_FORM_INVALID'));
      return throwError(() => new Error('School info form is invalid'));
    }
    if (this.form.pristine) {
      this.updateSchoolInfoLoading = false;
      return of(this.schoolInfo);
    }
    const updateDto: AntragTicketSchoolInfoUpdateDto = {
      schoolId: this.mapSchoolIdToUUID(this.getFormItem('schoolId')?.value),
      schoolYearId: this.mapSchoolYearIdToUUID(this.getFormItem('schoolYearId')?.value),
      classId: this.mapClassIdToUUID(this.getFormItem('classId')?.value),
      courseId: this.mapCourseIdToUUID(this.getFormItem('courseId')?.value),
      schoolDaysAWeek: this.getFormItem('schoolDaysAWeek')?.value,
      courseDate: this.getFormItem('courseDate')?.value,
      schoolLevel: this.getFormItem('schoolLevel')?.value
    }
    return this.antragTicketSchoolInfoService.updateSchoolInfoByAntrag(this.antragId, updateDto)
      .pipe(
        tap({
          next: schoolInfo => {
            this.schoolInfo = schoolInfo;
            this.schoolDaysAWeek = updateDto.schoolDaysAWeek;
            this.courseDate = updateDto.courseDate;
            this.updateForm();
            this.customToastService.showSuccess(this.translateService.instant('TICKETDETAIL.SCHOOL_INFO.UPDATE_SUCCESS'));
            this.updateSchoolInfoLoading = false;
          },
          error: err => {
            const error = JsonHelper.parseToJson(err.error)
            this.customToastService.showError(this.translateService.instant('TICKETDETAIL.SCHOOL_INFO.UPDATE_ERROR', {'errorCode': error?.reason || 'unknown'}));
            this.updateSchoolInfoLoading = false;
          },
          complete: () => {
            this.waitForSchoolInfoUpdate$.next();
            this.waitForSchoolInfoUpdate$.complete();
            this.waitForSchoolInfoUpdate$.unsubscribe();
            this.waitForSchoolInfoUpdate$ = new Subject<void>();
            this.form.markAsPristine();
          }
        })
      );
  }

  /**
   * Wrapper method for template calls to updateSchoolInfo.
   * Subscribes to the observable automatically.
   */
  onUpdateSchoolInfoClick(): void {
    this.updateSchoolInfo().subscribe({
      error: (err) => {
        // Error already handled in the tap operator
        console.error('School info update failed:', err);
      }
    });
  }

  mapSchoolIdToUUID(schoolId): string {
    const school: SchuleDTO = this.schoolList.find(s => s.id === schoolId);
    return school ? school.schuleId : null;
  }

  mapSchoolUUIDToId(schoolUUID): number {
    const school: SchuleDTO = this.schoolList.find(s => s.schuleId === schoolUUID);
    return school ? school.id : null;
  }

  mapSchoolYearIdToUUID(schoolYearId): string {
    const schoolYear: SchuljahrDTO = this.schoolYearList.find(sy => sy.id === schoolYearId);
    return schoolYear ? schoolYear.schuljahrId : null;
  }

  mapSchoolYearUUIDToId(schoolYearUUID): number {
    const schoolYear: SchuljahrDTO = this.schoolYearList.find(sy => sy.schuljahrId === schoolYearUUID);
    return schoolYear ? schoolYear.id : null;
  }

  mapClassIdToUUID(classId): string {
    const classObj: KlasseDTO = this.classList.find(c => c.id === classId);
    return classObj ? classObj.klasseId : null;
  }

  mapClassUUIDToId(classUUID): number {
    const classObj: KlasseDTO = this.classList.find(c => c.klasseId === classUUID);
    return classObj ? classObj.id : null;
  }

  mapCourseIdToUUID(courseId): string {
    const course: BildungsgangDTO = this.courseList.find(c => c.id === courseId);
    return course ? course.bildungsgangId : null;
  }

  mapCourseUUIDToId(courseUUID): number {
    const course: BildungsgangDTO = this.courseList.find(c => c.bildungsgangId === courseUUID);
    return course ? course.id : null;
  }

  schoolChange() {
    this.schoolChangeEvent.emit(this.getFormItem('schoolId')?.value);
  }

  getSchoolId() {
    return this.getFormItem('schoolId')?.value;
  }

  getSchoolYearId() {
    return this.getFormItem('schoolYearId')?.value;
  }

  resetFields() {
    this.form.patchValue({
      schoolType: null,
      classId: null,
      courseId: null,
      schoolYearId: null,
      schoolLevel: null,
      courseDate: null
    });
  }

  ngOnDestroy(): void {
    if (this.waitForSchoolInfoUpdate$) {
      this.waitForSchoolInfoUpdate$.unsubscribe();
    }
  }

  protected readonly KeyString = KeyString;
}
